import React, { useCallback, useEffect, useState } from "react";
import { ContainerPanel } from "../container-panel/container-panel";
import { SubTitle } from "@src/components/atoms/sub-title/sub-title";
import { ContainerPane } from "../container-pane/container-pane";
import { useI18N } from "@src/language/language-context";
import { FlexRowContainer } from "@src/components/templates/layouts/flex-row-container/flex-row-container";
import { SubHeader } from "@src/components/atoms/sub-header/sub-header";
import {
  ZitadelGatewayServiceUser,
  UserManagementListActiveRoleEnum,
  UserManagementListActiveRoleRenderDataType,
} from "@src/types/user-management";
import Spinner from "@src/components/atoms/spinner/spinner";
import styles from "./gateway-service-users-list-view.module.scss";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { GatewayServiceUsersList } from "../gateway-service-users-list/gateway-service-users-list";
import { getGatewayServiceUsersUseCase } from "@src/cloud-connect/user-manager/use-cases/get-gateway-service-users-use-case";
import { UserManagementPageSearchBar } from "../user-management-page-search/user-management-page-searchbar";

const cssClassName = "gateway-service-users-list-view";

interface GatewayServiceUsersListViewProps {
  gatewayServiceUsersListData?: ZitadelGatewayServiceUser[];
  userManagementListActiveRoleData: UserManagementListActiveRoleRenderDataType;
  onGatewayServiceUsersLoaded?: (
    gatewayServiceUsers: ZitadelGatewayServiceUser[],
  ) => void;
  onRenderDataChange: (
    userManagementListActiveRoleData: UserManagementListActiveRoleRenderDataType,
  ) => void;
}

export const GatewayServiceUsersListView = ({
  gatewayServiceUsersListData,
  userManagementListActiveRoleData,
  onGatewayServiceUsersLoaded,
  onRenderDataChange,
}: GatewayServiceUsersListViewProps) => {
  const i18n = useI18N();

  const [isLoading, setIsLoading] = useState(true);

  const texts = {
    subtitle: i18n("modules.gatewayServiceUsersListView.subtitle"),
    errorMessage: i18n("modules.gatewayServiceUsersListView.errorMessage"),
  };

  const loadGatewayServiceUsers = useCallback(
    async (signal?: AbortSignal) => {
      const requestSignal = signal ?? new AbortController().signal;

      setIsLoading(true);

      const result = await getGatewayServiceUsersUseCase(requestSignal);

      if (requestSignal?.aborted) return;

      if (result.success && result.data) {
        onGatewayServiceUsersLoaded?.(result.data);
        onRenderDataChange(result.data);
      } else {
        console.error("error loading gateway service users:", result.error);
        errorSnackbar(texts.errorMessage, 5000);
      }

      setIsLoading(false);
    },
    [onGatewayServiceUsersLoaded, texts.errorMessage],
  );

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    loadGatewayServiceUsers(signal);

    return () => {
      controller.abort();
    };
  }, [loadGatewayServiceUsers]);

  const handleStatusPatched = useCallback(
    (userId: string, newStatus: "ACTIVE" | "INACTIVE") => {
      const patchUserStatus = (users: ZitadelGatewayServiceUser[] = []) =>
        users.map((user) =>
          user.id === userId ? { ...user, status: newStatus } : user,
        );
      onGatewayServiceUsersLoaded?.(
        patchUserStatus(
          userManagementListActiveRoleData as ZitadelGatewayServiceUser[],
        ),
      );
      onRenderDataChange(patchUserStatus(gatewayServiceUsersListData ?? []));
    },
    [
      gatewayServiceUsersListData,
      onGatewayServiceUsersLoaded,
      onRenderDataChange,
      userManagementListActiveRoleData,
    ],
  );

  return (
    <ContainerPanel>
      <SubHeader>
        <FlexRowContainer justifyContent="space-between">
          <SubTitle text={texts.subtitle} />
        </FlexRowContainer>
      </SubHeader>
      <ContainerPane>
        <UserManagementPageSearchBar
          userManagementListActiveView={
            UserManagementListActiveRoleEnum.GATEWAY_SERVICE_USERS
          }
          userManagementListActiveRoleData={userManagementListActiveRoleData}
          onRenderDataChange={onRenderDataChange}
        />
        {isLoading ? (
          <div className={styles[`${cssClassName}__spinner-wrapper`]}>
            <Spinner />
          </div>
        ) : (
          <GatewayServiceUsersList
            gatewayServiceUsers={gatewayServiceUsersListData}
            onStatusPatched={handleStatusPatched}
          />
        )}
      </ContainerPane>
    </ContainerPanel>
  );
};
